import { useState } from 'react';
import {
  Carousel,
  CarouselNext,
  CarouselItem,
  CarouselContent,
  CarouselPrevious,
} from '@design-system/Carousel';
import { cn } from '@/utils/styles';
import { useGetHealthSummary } from '@viome/viome-base/src/usecase/Home/HomeQuery';
import { Skeleton } from '@design-system/Skeleton';
import { InfoSheet } from '@screens/HealthPages/InfoSheet';
import { INFO_SHEET_CONTENT } from '@/utils/InfoSheet/InfoSheetContent';
import { HealthInsightSheet } from './HealthInsightSheet';
import { Image } from '@design-system/Image';
import { getL2ImageUrl } from '@/utils/nutrition/getImageUrl';
import { DetectedIcon } from '@/components/icons/DetectedIcon';
import { SelfReportedIcon } from '@/components/icons/SelfReportedIcon';

const CARD_GRADIENTS = {
  detected:
    'bg-gradient-to-b from-[#2A4B99] to-[#6C88E3] backdrop-blur-[50px] shadow-[0px_1px_0px_0px_#FFFFFF0A_inset,_0px_1px_3px_0px_#0000001A]',
  other:
    'bg-gradient-to-b from-[#007F96] to-[#36B8BB] backdrop-blur-[50px] shadow-[0px_1px_0px_0px_#FFFFFF0A_inset,_0px_1px_3px_0px_#0000001A]',
};

export const HealthSummaryCards = () => {
  const [selectedInsight, setSelectedInsight] = useState<number | null>(null);
  const { data: healthSummaryData, isLoading, isError } = useGetHealthSummary();
  const sortedHealthSummaryData = healthSummaryData?.health_insights.sort(
    (a, b) => a.ordinal - b.ordinal,
  );

  if (isError) {
    return;
  }

  if (isLoading) {
    return (
      <div data-testid="home-healthOverviewSection">
        <div className="relative" data-testid="home-healthOverviewSection-header">
          <p className="mb-2 typography-h1 text-primary flex flex-row items-center gap-2" data-testid="home-healthOverviewSection-title">
            We’re targeting these issues
            <InfoSheet
              title={INFO_SHEET_CONTENT.summaryCards.title}
              description={INFO_SHEET_CONTENT.summaryCards.description}
              mode="light"
            />
          </p>
        </div>
        <div className="mt-8" data-testid="home-healthOverviewSection-carousel">
          <Carousel
            className="relative"
            opts={{
              align: 'start',
              dragFree: true,
            }}
          >
            <CarouselContent className="-ml-6 gap-2" data-testid="home-healthOverviewSection-carouselContent">
              {[1, 2, 3].map((_, index) => (
                <CarouselItem
                  key={`skeleton-${index}`}
                  className="pl-6 basis-80"
                >
                  <Skeleton
                    className={cn(
                      'w-80 h-48',
                      'rounded-md',
                      'bg-gradient-to-b from-[#2A4B99] to-[#6C88E3] backdrop-blur-[50px] shadow-[0px_1px_0px_0px_#FFFFFF0A_inset,_0px_1px_3px_0px_#0000001A]'
                    )}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden" />
            <CarouselNext className="hidden" />
          </Carousel>
        </div>
      </div>
    );
  }

  if (!healthSummaryData?.health_insights) return null;

  return (
    <div data-testid="home-healthOverviewSection">
      <div className="relative" data-testid="home-healthOverviewSection-header">
        <p className="mb-2 typography-h1 text-primary flex flex-row items-center gap-2" data-testid="home-healthOverviewSection-title">
          We’re targeting these issues
          <InfoSheet
            title={INFO_SHEET_CONTENT.summaryCards.title}
            description={INFO_SHEET_CONTENT.summaryCards.description}
            mode="light"
          />
        </p>
      </div>

      <div className="mt-8" data-testid="home-healthOverviewSection-carousel">
        <Carousel
          className="relative"
          opts={{
            align: 'start',
            dragFree: true,
          }}
        >
          <CarouselContent className="-ml-6 gap-2" data-testid="home-healthOverviewSection-carouselContent">
            {sortedHealthSummaryData?.map((insight, index) => (
              <CarouselItem
                key={`${insight.title}-${index}`}
                className="pl-6 basis-80"
                onClick={() => setSelectedInsight(index)}
                data-testid={`home-healthOverviewSection-carouselItem-${index}`}
              >
                <div
                  className={cn(
                    'w-80 h-full select-none',
                    'px-6 py-6',
                    'rounded-md',
                    'flex flex-col gap-4 justify-between cursor-pointer',
                    insight.header.toLowerCase() === 'detected'
                      ? CARD_GRADIENTS.detected
                      : CARD_GRADIENTS.other,
                  )}
                  data-testid={`home-healthOverviewSection-insightCard-${insight.title.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div data-testid={`home-healthOverviewSection-cardContent-${index}`}>
                    <div className="flex items-center" data-testid={`home-healthOverviewSection-cardHeader-${index}`}>
                      {(insight.header.toLowerCase() === 'detected') ? (
                        <DetectedIcon width={24} height={24} />
                      ) : (
                        <SelfReportedIcon width={24} height={24} />
                      )}
                      <span className="typography-caption1 text-white/50" data-testid={`home-healthOverviewSection-cardType-${index}`}>
                        {insight.header}
                      </span>
                    </div>

                    <h3 className="typography-headline3 text-white mt-2" data-testid={`home-healthOverviewSection-cardTitle-${index}`}>
                      {insight.title}
                    </h3>
                  </div>

                  <div className="flex items-center" data-testid={`home-healthOverviewSection-cardFooter-${index}`}>
                    <p className="typography-caption2 text-white">Related To</p>
                    <Image
                      src={getL2ImageUrl(insight.related_to_icon)}
                      alt={insight.header}
                      width={32}
                      height={32}
                    />
                    <p className="typography-caption2 text-white" data-testid={`home-healthOverviewSection-relatedTo-${index}`}>
                      {insight.related_to}
                    </p>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden" />
          <CarouselNext className="hidden" />
        </Carousel>
      </div>

      {selectedInsight !== null && (
        <HealthInsightSheet
          isOpen={selectedInsight !== null}
          onOpenChange={(open) => !open && setSelectedInsight(null)}
          insight={healthSummaryData.health_insights[selectedInsight]}
        />
      )}
    </div>
  );
};